import {cva} from 'class-variance-authority'

/**
 * Label (v3, MUI-aligned): a small uppercase status chip.
 *
 * - `severity` — semantic color role
 *   (brand/cta/error/favorite/flagged/gradient/info/neutral/primary/success/warning).
 * - `variant` — `filled` (solid color, white text) or `standard` (faint tint bg, colored text).
 *   Mirrors MUI Alert's `filled`/`standard`.
 *
 * Filled always uses white text. Standard text color varies per severity (some `main`,
 * some `dark`) to keep AA contrast on the faint background — matching Figma.
 *
 * `gradient` is the only severity that carries the CTA mint→green diagonal rather than a
 * flat token: `filled` puts it on the background, and `standard` puts it on the *text*.
 * The gradient-text lives on an inner span (`Label.tsx`) via `bg-clip-text`, so this
 * factory only sets the chip's faint background for that pair.
 */
export const LabelVariantsFactory = cva(
  'inline-flex items-center justify-center gap-[2px] overflow-clip rounded-sm p-1 font-sans text-[12px] font-bold uppercase leading-[14px] tracking-[1px] text-center',
  {
    variants: {
      severity: {
        brand: '',
        cta: '',
        error: '',
        favorite: '',
        flagged: '',
        gradient: '',
        info: '',
        neutral: '',
        primary: '',
        success: '',
        warning: '',
      },
      variant: {
        filled: 'text-white',
        standard: '',
      },
    },
    compoundVariants: [
      // --- filled: solid color background, white text ---
      {severity: 'brand', variant: 'filled', class: 'bg-secondary-dark'},
      {severity: 'cta', variant: 'filled', class: 'bg-cta'},
      {severity: 'error', variant: 'filled', class: 'bg-error'},
      {severity: 'favorite', variant: 'filled', class: 'bg-favorite'},
      {severity: 'flagged', variant: 'filled', class: 'bg-flagged'},
      {severity: 'gradient', variant: 'filled', class: 'bg-gradient-cta'},
      {severity: 'info', variant: 'filled', class: 'bg-info'},
      {severity: 'neutral', variant: 'filled', class: 'bg-neutral-600'},
      {severity: 'primary', variant: 'filled', class: 'bg-primary-dark'},
      {severity: 'success', variant: 'filled', class: 'bg-success'},
      {severity: 'warning', variant: 'filled', class: 'bg-warning'},
      // --- standard: faint tint background, colored text ---
      {severity: 'brand', variant: 'standard', class: 'bg-secondary-faint text-secondary'},
      {severity: 'cta', variant: 'standard', class: 'bg-cta-faint text-cta-dark'},
      {severity: 'error', variant: 'standard', class: 'bg-error-faint text-error'},
      {severity: 'favorite', variant: 'standard', class: 'bg-favorite-faint text-favorite-dark'},
      {severity: 'flagged', variant: 'standard', class: 'bg-flagged-faint text-flagged'},
      {severity: 'gradient', variant: 'standard', class: 'bg-cta-faint text-cta-contrast'},
      {severity: 'info', variant: 'standard', class: 'bg-info-faint text-info'},
      {severity: 'neutral', variant: 'standard', class: 'bg-neutral-100 text-neutral-600'},
      // `brand-primary-50` (#F6F9F9), NOT `primary-faint`: that token was retargeted to
      // #E4FCFF as the hover/selected tint for interactive controls (Button, IconButton,
      // TopNav, ButtonGroup), which is a visible pale cyan and wrong for a chip surface.
      // Figma names this background `brand-primary/50`, so point at it directly and stay
      // independent of any future primary/info faint churn.
      {severity: 'primary', variant: 'standard', class: 'bg-brand-primary-50 text-primary-dark'},
      {severity: 'success', variant: 'standard', class: 'bg-success-faint text-success'},
      {severity: 'warning', variant: 'standard', class: 'bg-warning-faint text-warning-dark'},
    ],
    defaultVariants: {
      severity: 'info',
      variant: 'standard',
    },
  },
)
